import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Pencil, Trash2, Plus } from 'lucide-react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useUpdateBoardGroup } from '@/features/boards/public';
import { priorityFromInt } from '../../adapters';
import { useTasksInfinite } from '../../hooks/useTasksInfinite';
import { computeAfterTaskId } from '../../utils/reorder';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useToast } from '@/shared/hooks/useToast';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useUIStore } from '@/core/stores/ui.store';
import { useProjectFields, useTaskFieldValuesBatch } from '@/features/custom-fields/public';
import { tasksService } from '../../api/tasks.service';
import { TASK_KEYS } from '../../hooks/queryKeys';
import { useTaskFieldUpdate } from '../../hooks/useTaskFieldUpdate';
import { useTaskBulkDelete } from '../../hooks/useTaskBulkDelete';
import { useOpenTask } from '../../hooks/useOpenTask';
import { useProjectStatuses } from '../../hooks/useProjectStatuses';
import { updateTaskEntity } from '../../hooks/useTaskEntityCache';
import type {
  GroupByOption,
  Task,
  TaskColumnId,
  TaskFilters,
  TaskListSortBy,
  TaskListViewConfig,
} from '../../types';
import { ColumnHeader, type PriorityDistribution } from './ColumnHeader';
import { SectionGroup } from './SectionGroup';
import { TaskRow } from './TaskRow';
import { InlineTaskRow } from './InlineTaskRow';
import { BulkEditDialog } from './BulkEditDialog';

interface GroupTableProps {
  projectId: string;
  boardId?: string;
  groupBy: GroupByOption;
  groupValue: string;
  groupLabel: string;
  groupColor?: string;
  config: TaskListViewConfig;
  /** columnId -> width in px — shared board-level setting, see `useSharedListViewConfig`. */
  columnWidths: Record<string, number>;
  filters: TaskFilters;
  onConfigChange: (config: TaskListViewConfig) => void;
  /** Shared (board-level) — visible columns and their order. */
  onColumnsChange: (columns: TaskColumnId[]) => void;
  /** Shared (board-level) — column widths. */
  onColumnWidthsChange: (widths: Record<string, number>) => void;
  defaultStatusId?: string;
  /** Omit the SectionGroup wrapper when groupBy is 'none' */
  unwrapped?: boolean;
}

function LoadMoreRow({
  groupValue,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  groupValue: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) {
  const { t } = useTranslation('tasks');

  if (!hasNextPage && !isFetchingNextPage) return null;

  return (
    <button
      type="button"
      data-testid={`tasks-group-table-sentinel-${groupValue}`}
      disabled={isFetchingNextPage}
      onClick={fetchNextPage}
      className="flex w-full items-center justify-center border-b border-border/50 py-2.5 text-xs font-medium text-primary transition-colors hover:bg-primary/5 hover:text-primary/80 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isFetchingNextPage ? t('listView.loadingMore') : t('listView.loadMore')}
    </button>
  );
}

/**
 * Maps the active List-view grouping to the extra create-task fields that should
 * inherit the clicked group's value, so a task created inside a group section
 * already belongs to it (Linear/ClickUp-style "create inside column" behavior).
 * The empty string is the API's "no group" / "no assignee" key. `CreateTaskPayload.groupId`
 * accepts `null` explicitly; `assigneeId` is `string | undefined`, and the adapter already
 * normalizes a missing assigneeId to `null` on the wire (`payload.assigneeId ?? null`), so
 * `undefined` here produces the identical API request as an explicit `null` would.
 */
function buildGroupCreateFields(groupBy: GroupByOption, groupValue: string) {
  if (groupBy === 'group') return { groupId: groupValue || null };
  if (groupBy === 'assignee') return { assigneeId: groupValue || undefined };
  if (groupBy === 'priority') return { priority: priorityFromInt(Number(groupValue)) };
  return {};
}

function BulkActionsBar({
  selectedCount,
  onEdit,
  onDelete,
  deleteDisabled,
  onClearSelection,
}: {
  selectedCount: number;
  onEdit: () => void;
  onDelete: () => void;
  deleteDisabled?: boolean;
  onClearSelection: () => void;
}) {
  const { t } = useTranslation('tasks');
  const deleteLabel = deleteDisabled
    ? t('listView.bulk.deleteBlockedNotArchived')
    : t('listView.bulk.delete');

  return (
    <div className="flex items-center gap-1 border-b border-border bg-primary/5 px-3 py-1.5">
      <span className="mr-2 text-xs font-medium text-primary">
        {t('listView.selectedCount', { count: selectedCount })}
      </span>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="tasks-bulk-edit"
            onClick={onEdit}
            className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={t('listView.bulk.edit')}
          >
            <Pencil className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{t('listView.bulk.edit')}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="tasks-bulk-delete"
            onClick={onDelete}
            disabled={deleteDisabled}
            className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
            aria-label={deleteLabel}
          >
            <Trash2 className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{deleteLabel}</TooltipContent>
      </Tooltip>

      <div className="mx-1 h-4 w-px bg-border" />

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="tasks-bulk-clear-selection"
            onClick={onClearSelection}
            className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={t('listView.clearSelection')}
          >
            <X className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{t('listView.clearSelection')}</TooltipContent>
      </Tooltip>
    </div>
  );
}

export function GroupTable({
  projectId,
  boardId,
  groupBy,
  groupValue,
  groupLabel,
  groupColor,
  config,
  columnWidths,
  filters,
  onConfigChange,
  onColumnsChange,
  onColumnWidthsChange,
  defaultStatusId,
  unwrapped = false,
}: GroupTableProps) {
  const { t } = useTranslation('tasks');
  const sortBy = config.sortBy;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const { toast } = useToast();

  const openTask = useOpenTask();
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const bulkDelete = useTaskBulkDelete(projectId);
  const { statuses } = useProjectStatuses(boardId ?? '');

  const {
    handleTitleSave,
    handleStatusChange,
    handlePriorityChange,
    handleAssigneeChange,
    handleDateChange,
    handleEstimatedChange,
    handleGroupChange,
  } = useTaskFieldUpdate({ projectId, boardId });

  const { mutate: doCreateTask } = useMutation({
    mutationFn: ({ title, parentId }: { title: string; parentId?: string }) =>
      tasksService.create(projectId, {
        title,
        boardId: boardId ?? '',
        statusId: defaultStatusId,
        parentId,
        ...buildGroupCreateFields(groupBy, groupValue),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TASK_KEYS.projectRoot(orgId, projectId),
        type: 'active',
      });
    },
  });

  function handleCreateTask(title: string) {
    setIsAddingTask(false);
    doCreateTask({ title });
  }

  const { mutate: doCreateSubtask } = useMutation({
    mutationFn: ({ parentId, title }: { parentId: string; title: string }) =>
      tasksService.createSubtask(parentId, { name: title }),
    onSuccess: (_data, { parentId }) => {
      void queryClient.invalidateQueries({
        queryKey: TASK_KEYS.projectRoot(orgId, projectId),
        type: 'active',
      });
      // The expanded-row subtask list reads TASK_KEYS.subtasks directly, not the
      // projectRoot-prefixed cursor queries — without this it stays stale after
      // an inline subtask creation and the new row never appears until reload.
      void queryClient.invalidateQueries({ queryKey: TASK_KEYS.subtasks(orgId, parentId) });
      void queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(orgId, parentId) });
      void queryClient.invalidateQueries({ queryKey: TASK_KEYS.treeRoot(orgId) });
    },
  });

  function handleSubtaskCreate(parentId: string, title: string) {
    doCreateSubtask({ parentId, title });
  }

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    queryKey,
  } = useTasksInfinite({
    projectId,
    boardId,
    groupBy,
    groupValue,
    sortBy,
    showCompleted: config.showCompleted,
    includeArchived: config.showArchived,
    filters,
  });

  const tasks = data?.pages.flatMap((p) => p.items) ?? [];
  const totalCount = data?.pages[0]?.total ?? 0;

  const { mutate: doReorderTasks } = useMutation({
    mutationFn: (orderedTaskIds: string[]) =>
      tasksService.reorderTasks({ boardId: boardId ?? '', statusId: groupValue, orderedTaskIds }),
    onMutate: async (orderedTaskIds: string[]) => {
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData<{ pages: { items: Task[] }[]; pageParams: unknown[] }>(
        queryKey,
        (old) => {
          if (!old) return old;
          const allItems = old.pages.flatMap((p) => p.items);
          const idToTask = new Map(allItems.map((t) => [t.id, t]));
          const reordered = orderedTaskIds
            .map((id) => idToTask.get(id))
            .filter((t): t is Task => !!t);
          return {
            ...old,
            pages: old.pages.map((p, i) =>
              i === 0 ? { ...p, items: reordered } : { ...p, items: [] },
            ),
          };
        },
      );
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(queryKey, ctx.snapshot);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: doMoveTaskIntoGroup } = useMutation({
    mutationFn: ({ taskId, afterTaskId }: { taskId: string; afterTaskId: string | null }) =>
      tasksService.moveTask(taskId, { statusId: groupValue, afterTaskId }),
    onMutate: async ({ taskId }: { taskId: string; afterTaskId: string | null }) => {
      const status = statuses.find((s) => s.id === groupValue);
      if (status) {
        updateTaskEntity(queryClient, orgId, taskId, (task) => ({ ...task, status }));
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TASK_KEYS.projectRoot(orgId, projectId) });
    },
  });

  useEffect(() => {
    if (groupBy !== 'status') return;

    return monitorForElements({
      onDrop({ source, location }) {
        const targets = location.current.dropTargets;
        if (targets.length === 0) return;

        const sourceData = source.data as { taskId?: string; statusId?: string };
        const sourceTaskId = sourceData.taskId;
        const sourceStatusId = sourceData.statusId;
        if (!sourceTaskId || !sourceStatusId) return;

        const targetData = targets[0].data as { taskId?: string; statusId?: string };
        const targetTaskId = targetData.taskId;
        const targetStatusId = targetData.statusId;
        // Only the GroupTable instance whose section the drop landed in should act.
        if (!targetStatusId || targetStatusId !== groupValue) return;
        if (sourceTaskId === targetTaskId) return;

        // Refuse to act on a partially-loaded group — see "Current state" rationale.
        if (tasks.length < totalCount) return;

        const targetIndex = tasks.findIndex((t) => t.id === targetTaskId);
        if (targetIndex === -1) return;

        if (sourceStatusId === groupValue) {
          const others = tasks.filter((t) => t.id !== sourceTaskId).map((t) => t.id);
          others.splice(targetIndex, 0, sourceTaskId);
          doReorderTasks(others);
        } else {
          const afterTaskId = computeAfterTaskId(
            tasks.filter((t) => t.id !== sourceTaskId),
            targetIndex,
          );
          doMoveTaskIntoGroup({ taskId: sourceTaskId, afterTaskId });
        }
      },
    });
  }, [groupBy, groupValue, tasks, totalCount, doReorderTasks, doMoveTaskIntoGroup]);

  const timeFormat = useUIStore((s) => s.timeFormat);
  const dateFormat = useUIStore((s) => s.dateFormat);
  const { data: fieldsResult } = useProjectFields(projectId);
  const fieldMap = Object.fromEntries((fieldsResult?.items ?? []).map((f) => [f.id ?? '', f]));

  // One request for every visible task × visible custom-field column, instead
  // of one per (task, field) pair — see useTaskFieldValuesBatch.
  const visibleFieldIds = config.visibleColumns.filter((col) => fieldMap[col]);
  const { data: customFieldValuesByTask } = useTaskFieldValuesBatch(
    projectId,
    tasks.map((t) => t.id),
    visibleFieldIds,
  );

  const priorityDistribution = tasks.reduce<PriorityDistribution>((acc, task) => {
    if (task.priority) {
      acc[task.priority] = (acc[task.priority] ?? 0) + 1;
    }
    return acc;
  }, {});

  function handleSortChange(next: TaskListSortBy | undefined) {
    onConfigChange({ ...config, sortBy: next });
  }

  function handleSelect(id: string, selected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function handleSelectAll(checked: boolean) {
    setSelectedIds(checked ? new Set(tasks.map((task) => task.id)) : new Set());
  }

  const { mutate: doRenameGroup } = useUpdateBoardGroup(boardId ?? '');

  function handleRenameGroup(name: string) {
    if (!groupValue) return;
    doRenameGroup({ groupId: groupValue, data: { name } });
  }

  function handleBulkEdit() {
    setBulkEditOpen(true);
  }

  async function handleBulkDelete() {
    const result = await confirm({
      title: t('listView.bulk.deleteConfirmTitle', { count: selectedIds.size }),
      description: t('listView.bulk.deleteConfirmDesc', { count: selectedIds.size }),
      confirmLabel: t('listView.bulk.deleteConfirmAction'),
      cancelLabel: t('listView.bulk.deleteConfirmCancel'),
      variant: 'destructive',
    });

    if (!result.confirmed) return;

    const { results } = await bulkDelete.mutateAsync([...selectedIds]);
    const successCount = results.filter((r) => r.success).length;
    const failed = results.length - successCount;
    if (failed > 0) {
      toast.error({ title: t('listView.bulk.deletePartialError', { failed }) });
    } else {
      toast.success({ title: t('listView.bulk.deleteSuccess', { count: successCount }) });
    }
    setSelectedIds(new Set());
  }

  const table = (
    <>
      <div data-testid={`tasks-group-table-${groupValue}`}>
        <ColumnHeader
          visibleColumns={config.visibleColumns}
          columnWidths={columnWidths}
          sortBy={sortBy}
          priorityDistribution={tasks.length > 0 ? priorityDistribution : undefined}
          selectedCount={selectedIds.size}
          totalCount={tasks.length}
          projectId={projectId}
          onSelectAll={handleSelectAll}
          onSortChange={handleSortChange}
          onColumnsChange={onColumnsChange}
          onColumnWidthsChange={onColumnWidthsChange}
        />

        {selectedIds.size > 0 && (
          <BulkActionsBar
            selectedCount={selectedIds.size}
            onEdit={handleBulkEdit}
            onDelete={handleBulkDelete}
            deleteDisabled={tasks.some((task) => selectedIds.has(task.id) && !task.isArchived)}
            onClearSelection={() => setSelectedIds(new Set())}
          />
        )}

        {isError ? (
          <div className="px-10 py-3 text-xs text-muted-foreground">
            {t('listView.loadError')}{' '}
            <button
              type="button"
              onClick={() => refetch()}
              className="font-medium text-primary hover:underline"
              data-testid={`tasks-group-table-retry-${groupValue}`}
            >
              {t('retry')}
            </button>
          </div>
        ) : isLoading ? (
          <p className="px-10 py-3 text-xs text-muted-foreground">{t('loading')}</p>
        ) : tasks.length === 0 ? (
          <p className="px-10 py-3 text-xs text-muted-foreground">{t('listView.empty')}</p>
        ) : (
          tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              boardId={boardId}
              visibleColumns={config.visibleColumns}
              columnWidths={columnWidths}
              timeFormat={timeFormat}
              dateFormat={dateFormat}
              fieldMap={fieldMap}
              customFieldValuesByTask={customFieldValuesByTask}
              isSelected={selectedIds.has(task.id)}
              isSubtaskSelected={(id) => selectedIds.has(id)}
              hideDragHandle={groupBy !== 'status'}
              onSelect={handleSelect}
              onOpenDetail={openTask}
              onTitleSave={handleTitleSave}
              onStatusChange={handleStatusChange}
              onPriorityChange={handlePriorityChange}
              onAssigneeChange={handleAssigneeChange}
              onDateChange={handleDateChange}
              onEstimatedChange={handleEstimatedChange}
              onSubtaskCreate={handleSubtaskCreate}
              onGroupChange={handleGroupChange}
            />
          ))
        )}

        {isAddingTask && (
          <InlineTaskRow
            visibleColumns={config.visibleColumns}
            columnWidths={columnWidths}
            onConfirm={handleCreateTask}
            onCancel={() => setIsAddingTask(false)}
          />
        )}

        {!isLoading && (
          <button
            type="button"
            data-testid={`tasks-group-table-add-task-${groupValue}`}
            onClick={() => setIsAddingTask(true)}
            className="flex w-full items-center gap-1.5 border-b border-border/50 px-10 py-2 text-xs text-muted-foreground transition-colors hover:bg-accent/30 hover:text-foreground"
          >
            <Plus className="size-3.5" />
            {t('listView.addTask')}
          </button>
        )}

        <LoadMoreRow
          groupValue={groupValue}
          hasNextPage={hasNextPage ?? false}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      </div>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}

      {boardId && (
        <BulkEditDialog
          taskIds={[...selectedIds]}
          boardId={boardId}
          projectId={projectId}
          open={bulkEditOpen}
          onOpenChange={setBulkEditOpen}
          onSuccess={() => setSelectedIds(new Set())}
        />
      )}
    </>
  );

  if (unwrapped) return table;

  // Empty string is the API key for "no group" — those groups can't be renamed
  const isGroupByGroup = groupBy === 'group' && groupValue !== '';

  return (
    <SectionGroup
      groupId={groupValue}
      label={groupLabel}
      color={groupColor}
      count={totalCount}
      onSelectAll={isGroupByGroup ? () => handleSelectAll(true) : undefined}
      onRename={isGroupByGroup ? handleRenameGroup : undefined}
    >
      {table}
    </SectionGroup>
  );
}
